import { useMutation } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { DeviceForbiddenError } from '../../../lib/deviceAuthError'
import type { CashierMemberProfile } from './useMemberSearch'

export class InvalidTokenError extends Error {
  constructor() { super('invalid_token') }
}

export class TokenExpiredError extends Error {
  constructor() { super('token_expired') }
}

export class QrMemberNotFoundError extends Error {
  constructor() { super('member_not_found') }
}

export class AlreadyConsumedError extends Error {
  constructor() { super('already_consumed') }
}

export class NoLongerActiveError extends Error {
  constructor() { super('no_longer_active') }
}

export interface CashierRedemptionScan {
  id: string
  memberId: string
  firstName: string | null
  lastName: string | null
  email: string | null
  profilePhotoUrl: string | null
  phone: string | null
  redemptionTokenId: string
  rewardItemId: string
  rewardItemName: { en: string; sv: string }
  quantity: number
  expiresAt: number
  availableRewards: number
}

export type VerifyQrResult =
  | { qrType: 'member'; profile: CashierMemberProfile }
  | { qrType: 'redemption'; scan: CashierRedemptionScan }

interface VerifyQrResponse {
  error?: string
  qr_type?: string
  id?: string
  member_id?: string
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  profile_photo_url?: string | null
  phone?: string | null
  redemption_token_id?: string
  reward_item_id?: string
  reward_item_name?: { en: string; sv: string }
  quantity?: number
  expires_at?: number
  available_rewards?: number
}

async function verifyQrToken(token: string): Promise<VerifyQrResult> {
  const { data, error } = await supabase.rpc('verify_qr', { p_token: token }) as {
    data: VerifyQrResponse | null
    error: Error | null
  }

  if (error) throw error
  if (!data) throw new Error('verify_failed')

  if (data.error === 'token_expired')  throw new TokenExpiredError()
  if (data.error === 'member_not_found') throw new QrMemberNotFoundError()
  if (data.error === 'already_consumed') throw new AlreadyConsumedError()
  if (data.error === 'no_longer_active') throw new NoLongerActiveError()
  if (data.error === 'forbidden') throw new DeviceForbiddenError()
  if (data.error) throw new InvalidTokenError()

  if (data.qr_type === 'member') {
    return {
      qrType: 'member',
      profile: {
        id:             data.id!,
        memberId:       data.member_id!,
        firstName:      data.first_name ?? null,
        lastName:       data.last_name ?? null,
        email:          data.email ?? null,
        profilePhotoUrl: data.profile_photo_url ?? null,
      },
    }
  }

  return {
    qrType: 'redemption',
    scan: {
      id:                data.id!,
      memberId:          data.member_id!,
      firstName:         data.first_name ?? null,
      lastName:          data.last_name ?? null,
      email:             data.email ?? null,
      profilePhotoUrl:   data.profile_photo_url ?? null,
      phone:             data.phone ?? null,
      redemptionTokenId: data.redemption_token_id!,
      rewardItemId:      data.reward_item_id!,
      rewardItemName:    data.reward_item_name!,
      quantity:          data.quantity!,
      expiresAt:         data.expires_at!,
      availableRewards:  data.available_rewards!,
    },
  }
}

export function useVerifyQr() {
  return useMutation({ mutationFn: verifyQrToken })
}